import { supabase } from './supabase';
import { edgeErrorMessage } from './canvasPipeline';
import { jobStatusLabel, waitForJobResultUrl } from './jobsApi';
import type { Caption } from '../types/editor';
import type { BoardAudioKind, BoardAudioNode } from './canvasBoard';

/** Seconds of an audio file from its metadata; 0 when unreadable. */
export function audioUrlSeconds(url: string): Promise<number> {
  return new Promise((resolve) => {
    const el = document.createElement('audio');
    el.preload = 'metadata';
    let settled = false;
    const finish = (d: number) => {
      if (settled) return;
      settled = true;
      el.removeAttribute('src');
      resolve(Number.isFinite(d) && d > 0 ? d : 0);
    };
    el.onloadedmetadata = () => finish(el.duration);
    el.onerror = () => finish(0);
    window.setTimeout(() => finish(0), 10000);
    el.src = url;
  });
}

/** Enqueues one sound on `create-audio` (debits there) and waits for the file. */
export async function generateBoardAudio(args: {
  orgId: string;
  kind: BoardAudioKind;
  text: string;
  voiceModelId: string;
  voiceId?: string;
  loop?: boolean;
  sourceAudioUrl?: string;
  /** Target length for music models that accept it. */
  durationSeconds?: number;
  title?: string;
  onStatus?: (label: string) => void;
}): Promise<{ url: string; credits: number; seconds: number }> {
  const body = {
    organization_id: args.orgId,
    prompt: args.text,
    voice_model_id: args.voiceModelId,
    voice_id: args.voiceId,
    audio_kind: args.kind,
    sound_loop: args.loop === true,
    upload_url: args.sourceAudioUrl,
    title: args.title,
    duration_seconds: args.durationSeconds ? Math.ceil(args.durationSeconds) : undefined,
  };
  const { data, error } = await supabase.functions.invoke('create-audio', { body });
  if (error || data?.error) throw new Error(await edgeErrorMessage(error, data));
  const jobId = String(data?.job_id || '');
  if (!jobId) throw new Error('create-audio não devolveu job_id.');
  args.onStatus?.('Na fila…');
  const url = await waitForJobResultUrl(jobId, {
    timeoutMs: 10 * 60 * 1000,
    onStatus: (job) => args.onStatus?.(jobStatusLabel(job)),
  });
  return { url, credits: Number(data?.credits_charged) || 0, seconds: await audioUrlSeconds(url) };
}

const CAPTION_MAX_CHARS = 42;

/** Short subtitle lines from a narration, broken at punctuation, then at word boundaries. */
export function captionLines(text: string): string[] {
  const sentences = text.replace(/\s+/g, ' ').trim().split(/(?<=[.!?;:,])\s+/).filter(Boolean);
  const lines: string[] = [];
  for (const sentence of sentences) {
    let line = '';
    for (const word of sentence.split(' ')) {
      if (line && (line + ' ' + word).length > CAPTION_MAX_CHARS) {
        lines.push(line);
        line = word;
      } else {
        line = line ? `${line} ${word}` : word;
      }
    }
    if (line) lines.push(line);
  }
  return lines;
}

/**
 * Subtitles for the narration nodes: each line gets a slice of the track's real length,
 * proportional to its characters, shifted by the track's start and speed.
 */
export function narrationCaptions(voices: BoardAudioNode[], videoSeconds: number, position?: Caption['position']): Caption[] {
  const out: Caption[] = [];
  for (const v of voices) {
    if (v.audioKind !== 'voice' || v.muted || !v.audioUrl || !v.seconds) continue;
    const lines = captionLines(v.text);
    const total = lines.reduce((sum, l) => sum + l.length, 0);
    if (!total) continue;
    const span = v.seconds / (v.speed && v.speed > 0 ? v.speed : 1);
    let t = v.startOffset || 0;
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      const d = (span * line.length) / total;
      const start = t;
      const end = Math.min(videoSeconds, t + d);
      t += d;
      if (start >= videoSeconds) break;
      out.push({ id: `${v.id}_${i}`, text: line, startTime: +start.toFixed(2), endTime: +end.toFixed(2), isImpactPhrase: false, position });
    }
  }
  return out;
}
